import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ThemeToggle from './ui/ThemeToggle';

interface SidebarProps {
  onClose: () => void;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
}

interface NavigationItem {
  id: number;
  name: string;
  path: string;
  icon: string;
  permission: string | null;
  level: number;
  parent_id: number | null;
  children?: NavigationItem[];
  isExpanded?: boolean;
  position?: number;
}

const Sidebar: React.FC<SidebarProps> = ({ onClose, collapsed = false, onToggleCollapse }) => {
  const { user, logout, canAccessModule, accessibleModules } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set());

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const toggleExpanded = (itemId: number) => {
    setExpandedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  // Auto-expand parent items when navigating to child pages
  useEffect(() => {
    if (!accessibleModules || accessibleModules.length === 0) return;

    const currentPath = location.pathname;
    const modulesToExpand = new Set<number>();

    // Find the current module and its parents
    const findModuleAndParents = (modules: any[], targetPath: string): number[] => {
      const moduleMap = new Map<number, any>();
      modules.forEach(module => moduleMap.set(module.id, module));

      // Find the target module
      const targetModule = modules.find(module => `/${module.route}` === targetPath);
      if (!targetModule) return [];

      const parentIds: number[] = [];
      let currentModule = targetModule;

      // Walk up the hierarchy to find all parent IDs
      while (currentModule.parent_id !== null) {
        parentIds.push(currentModule.parent_id);
        currentModule = moduleMap.get(currentModule.parent_id);
        if (!currentModule) break;
      }

      return parentIds;
    };

    const parentIds = findModuleAndParents(accessibleModules, currentPath);
    parentIds.forEach(id => modulesToExpand.add(id));

    setExpandedItems(modulesToExpand);
  }, [location.pathname, accessibleModules]);

  // Convert accessible modules to navigation structure
  const buildNavigationItems = (): NavigationItem[] => {
    if (!accessibleModules || accessibleModules.length === 0) {
      return [];
    }

    // Create a map of all modules by ID
    const modulesMap = new Map<number, NavigationItem>();
    
    // First pass: create all items
    accessibleModules.forEach(module => {
      const icon = getIconForRoute(module.route);
      
      modulesMap.set(module.id, {
        id: module.id,
        name: module.title,
        path: `/${module.route}`,
        icon,
        permission: module.route,
        level: parseInt(module.level),
        parent_id: module.parent_id,
        children: [],
        isExpanded: expandedItems.has(module.id),
        position: module.position
      });
    });

    // Second pass: build hierarchy
    const rootItems: NavigationItem[] = [];
    
    modulesMap.forEach(item => {
      if (item.parent_id === null) {
        rootItems.push(item);
      } else {
        const parent = modulesMap.get(item.parent_id);
        if (parent) {
          parent.children = parent.children || [];
          parent.children.push(item);
        }
      }
    });

    // Sort items by position and level
    const sortItems = (items: NavigationItem[]): NavigationItem[] => {
      return items.sort((a, b) => {
        if (a.level !== b.level) return a.level - b.level;
        return (a.position || 0) - (b.position || 0);
      }).map(item => ({
        ...item,
        children: item.children ? sortItems(item.children) : []
      }));
    };

    return sortItems(rootItems);
  };

  const getIconForRoute = (route: string): string => {
    const iconMap: Record<string, string> = {
      'dashboard': '📊',
      'system': '⚙️',
      'users': '👥',
      'groups': '🏢',
      'modules': '🔧',
      'calls': '📞',
      'call-logs': '📋',
      'contacts': '📇',
      'reports': '📈',
      'agent-reports': '🏆',
      'call-analytics': '📊',
      'agent-performance-report': '📊',
      'agent-call-count-report': '📞',
      'agent-call-list-report': '📋',
      'call-statistics': '📈',
      'agent-call-history': '📜',
      'user-permissions': '🔐',
      'user-activity': '📊',
      'group-permissions': '🔐',
      'group-members': '👥',
      'profile': '👤',
    };
    
    return iconMap[route] || '📄';
  };

  const renderNavigationItem = (item: NavigationItem, depth: number = 0): React.ReactNode => {
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedItems.has(item.id);
    const isActiveItem = isActive(item.path);
    
    // Check if user has permission to access this item
    if (item.permission && !canAccessModule(item.permission)) {
      return null;
    }

    return (
      <div key={item.id}>
        <div
          className={`
            flex items-center justify-between px-3 py-2 rounded-md text-sm font-medium transition-colors cursor-pointer
            ${collapsed ? 'justify-center' : ''}
            ${isActiveItem
              ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
              : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
            }
          `}
          style={{ paddingLeft: collapsed ? '0.75rem' : `${0.75 + depth * 1.5}rem` }}
        >
          <div
            className="flex items-center space-x-3 flex-1"
            onClick={() => {
              // Always navigate to the item, regardless of whether it has children
              navigate(item.path);
              // Only close sidebar on mobile
              if (window.innerWidth < 1024) {
                onClose();
              }
            }}
          >
            <span className="text-lg">{item.icon}</span>
            {!collapsed && <span>{item.name}</span>}
          </div>
          
          {!collapsed && hasChildren && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                toggleExpanded(item.id);
              }}
              className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded"
            >
              <span className={`transform transition-transform ${isExpanded ? 'rotate-180' : ''}`}>
                ▼
              </span>
            </button>
          )}
        </div>
        
        {!collapsed && hasChildren && isExpanded && (
          <div className="ml-4">
            {item.children!.map(child => renderNavigationItem(child, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  const navigationItems = buildNavigationItems();

  return (
    <div className={`
      flex-shrink-0 bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-700 
      transition-all duration-300 ease-in-out h-screen flex flex-col
      ${collapsed ? 'w-16' : 'w-64'}
    `}>
      {/* Header */}
      <div className={`flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 ${collapsed ? 'p-2' : ''}`}>
        {!collapsed && (
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">
            ATNR System
          </h1>
        )}
        <div className="flex items-center space-x-2">
          <ThemeToggle />
          {onToggleCollapse && (
            <button
              onClick={onToggleCollapse}
              className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              {collapsed ? '→' : '←'}
            </button>
          )}
        </div>
      </div>

      {/* User Info */}
      {!collapsed && user && (
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white text-sm font-medium">
              {user.name.charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                {user.name}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                {user.user_type}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Navigation */}
      <nav className={`flex-1 p-4 space-y-1 overflow-y-auto ${collapsed ? 'p-2' : ''}`}>
        {navigationItems.length === 0 ? (
          <div className="text-center py-4">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              No modules available
            </p>
          </div>
        ) : (
          navigationItems.map(item => renderNavigationItem(item))
        )}
      </nav>

      {/* Footer */}
      <div className={`p-4 border-t border-gray-200 dark:border-gray-700 ${collapsed ? 'p-2' : ''}`}>
        <div className={`flex items-center justify-between ${collapsed ? 'justify-center' : ''}`}>
          <ThemeToggle />
          {!collapsed && (
            <button
              onClick={handleLogout}
              className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 dark:text-gray-300 dark:hover:text-gray-100"
            >
              <span>🚪</span>
              <span>Logout</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
